import { jsx, jsxs } from "react/jsx-runtime";
import styled, { css } from "styled-components";
import { KnowledgeBaseIcon, IconSize, ICON_SELECTOR, CheckboxIcon, CloseIcon, SpinnerIcon, RadioSelectedIcon, RadioUnselectedIcon, Icon, IconName, ChevronUpIcon, ChevronDownIcon } from "@ardoq/icons";
import { colors, s4, s8, s32, s16, r4, zIndex, fade, space, shadowS, r8, s24 } from "@ardoq/design-tokens";
import { WithPopover, PopoverPlacement } from "@ardoq/popovers";
import { Space, basicLinkCSS, warningCSS, errorCSS, focusRingCSS, getFocusRingBorderCSS } from "@ardoq/style-helpers";
import { fontMixins, caption, text1, Text, normal16, text2Bold, text2 } from "@ardoq/typography";
import { useFocusRing } from "@react-aria/focus";
import { useRef, useEffect, useState, forwardRef, createContext, useContext, useCallback, useId, useImperativeHandle, useMemo } from "react";
import { BUTTON_SELECTOR } from "@ardoq/button";
import { Box, FlexBox } from "@ardoq/layout";
import autosize from "autosize";
import { without, uniq, pick, debounce, isNumber } from "lodash";
import { getIsCssColorValue } from "@ardoq/color-helpers";
import { useSliderState } from "@react-stately/slider";
import { useNumberFormatter } from "@react-aria/i18n";
import { useSliderThumb, useSlider } from "@react-aria/slider";
import { VisuallyHidden } from "@react-aria/visually-hidden";
import { mergeProps } from "@react-aria/utils";
const LabelHelpIcon = styled(KnowledgeBaseIcon).attrs({
  iconSize: IconSize.MEDIUM
})`
  color: ${colors.iconAction};
  cursor: pointer;
  &:hover {
    color: ${colors.iconActionHover};
  }
`;
const PopoverWrapper = styled(Space).attrs({
  $isVertical: true
})`
  width: ${"279px"};
`;
const LabelHelpIconWithPopover = ({
  content
}) => {
  return /* @__PURE__ */ jsx(
    WithPopover,
    {
      content: () => /* @__PURE__ */ jsx(PopoverWrapper, { children: content }),
      preferredPlacement: PopoverPlacement.TOP,
      children: /* @__PURE__ */ jsx(LabelHelpIcon, {})
    }
  );
};
const LABEL_ICON_SELECTOR = LabelHelpIcon.toString();
const LabelStyle = styled.label`
  display: flex;
  align-items: center;
  ${fontMixins.semibold16};
  color: ${({ $isDisabled }) => $isDisabled ? colors.textDisabled : colors.textDefault};
  ${({ $isDisabled }) => $isDisabled && css`
      pointer-events: none;
      ${ICON_SELECTOR} {
        color: ${colors.iconActionDisabled};
      }
    `}

  width: ${({ $width }) => $width ?? "100%"};
`;
const OptionalStyle = styled.label`
  color: ${colors.textSubtle};
  padding-left: ${s4};
  margin-bottom: 0;
`;
const LABEL_SELECTOR = LabelStyle.toString();
const Label = ({
  children,
  id,
  isOptional,
  inputId,
  isDisabled,
  width,
  popoverHelpContent
}) => {
  return /* @__PURE__ */ jsxs(
    LabelStyle,
    {
      htmlFor: inputId,
      $isDisabled: isDisabled,
      $width: width,
      id,
      children: [
        children,
        isOptional && /* @__PURE__ */ jsx(OptionalStyle, { children: "(optional)" }),
        popoverHelpContent && /* @__PURE__ */ jsx(LabelHelpIconWithPopover, { content: popoverHelpContent })
      ]
    }
  );
};
const FocusableCheckboxIcon = ({
  isChecked,
  isMixed,
  isFocused
}) => /* @__PURE__ */ jsx(CheckboxIcon, { mixed: isMixed, checked: isChecked, isFocused });
var FormSize = /* @__PURE__ */ ((FormSize2) => {
  FormSize2["DEFAULT"] = "448px";
  FormSize2["FULL"] = "100%";
  FormSize2["AUTO"] = "auto";
  FormSize2["SMALL"] = "150px";
  FormSize2["MEDIUM"] = "224px";
  return FormSize2;
})(FormSize || {});
const InputHintMessage = styled(Box).attrs({
  marginTop: "small"
})`
  ${caption};
  color: ${colors.textSubtle};
  word-break: break-word;
`;
const InputErrorMessage = styled(InputHintMessage)`
  color: ${colors.textError};
`;
const InputWarningMessage = styled(InputHintMessage)`
  color: ${colors.textWarning};
`;
const useTextAreaAutosize = (value) => {
  const ref = useRef(null);
  useEffect(() => {
    const textarea = ref.current;
    if (!textarea) return;
    autosize(textarea);
    return () => {
      autosize.destroy(textarea);
    };
  }, [value]);
  return { ref };
};
function useGroupState({
  defaultValue,
  value,
  onValueChange,
  onChange,
  ...rest
}) {
  const [isInputControlled] = useState(value !== void 0);
  const changeCallback = (newValue) => {
    onValueChange == null ? void 0 : onValueChange(newValue);
    onChange == null ? void 0 : onChange(newValue);
  };
  const [currentValueFromHook, setCurrentValueFromHook] = useState(
    defaultValue || null
  );
  const hasError = rest.hasError || Boolean(rest.errorMessage);
  const hasWarning = rest.hasWarning || Boolean(rest.warningMessage);
  const { currentValue, setCurrentValue } = isInputControlled ? { currentValue: value, setCurrentValue: changeCallback } : {
    currentValue: currentValueFromHook,
    setCurrentValue: (newValue) => {
      setCurrentValueFromHook(newValue);
      changeCallback(newValue);
    }
  };
  return {
    currentValue,
    setCurrentValue,
    ...rest,
    hasError,
    hasWarning
  };
}
const getChildElementId = (inputId, suffix) => inputId !== void 0 ? `${inputId}-${suffix}` : void 0;
const FieldWrapper = styled.div`
  width: ${({ $formSize = FormSize.AUTO }) => $formSize};
  ${({ $alignItemsCenter }) => $alignItemsCenter && "display: flex; align-items: center;"}

  ${LABEL_ICON_SELECTOR} {
    margin-left: ${s4};
  }

  ${LABEL_SELECTOR} + ${BUTTON_SELECTOR} {
    margin-top: 0;
  }

  & > ${BUTTON_SELECTOR} {
    margin-top: ${s8};
  }
`;
const HelperTextWrapper = styled.div`
  ${caption};
  color: ${({ $isDisabled }) => $isDisabled ? colors.textDisabled : colors.textSubtle};
  margin-bottom: ${s8};
  word-break: break-word;

  & > a {
    ${basicLinkCSS}
  }
`;
const FIELD_SELECTOR = FieldWrapper.toString();
const CloseFieldIcon = styled(CloseIcon)`
  cursor: pointer;
  color: ${colors.iconAction};
  &:hover {
    color: ${colors.iconActionHover};
  }
`;
const ChildrenWrapper = styled.div`
  & > ${BUTTON_SELECTOR} {
    margin-top: ${s8};
  }
`;
const Field = forwardRef(
  ({
    children,
    label,
    labelId,
    inputId,
    isDisabled,
    isOptional,
    helperText,
    errorMessage,
    hintMessage,
    warningMessage,
    onCloseFieldClick,
    closeFieldIconTooltip,
    __DS_ONLY_fieldStyle,
    dataTestId,
    popoverContent,
    popoverHelpContent,
    formSize,
    ...rest
  }, ref) => /* @__PURE__ */ jsxs(
    FieldWrapper,
    {
      ref,
      style: __DS_ONLY_fieldStyle,
      "data-test-id": dataTestId,
      "data-testid": dataTestId,
      $formSize: formSize,
      ...rest,
      children: [
        label && /* @__PURE__ */ jsxs(FlexBox, { paddingBottom: "small", justify: "space-between", children: [
          /* @__PURE__ */ jsx(
            Label,
            {
              id: labelId,
              inputId,
              isDisabled,
              isOptional,
              popoverHelpContent,
              children: label
            }
          ),
          onCloseFieldClick && /* @__PURE__ */ jsx(FlexBox, { marginLeft: "xsmall", children: /* @__PURE__ */ jsx(
            CloseFieldIcon,
            {
              onClick: onCloseFieldClick,
              "data-tooltip-text": closeFieldIconTooltip
            }
          ) })
        ] }),
        helperText && /* @__PURE__ */ jsx(
          HelperTextWrapper,
          {
            $isDisabled: isDisabled,
            id: getChildElementId(inputId, "help-text"),
            children: helperText
          }
        ),
        /* @__PURE__ */ jsx(
          WithPopover,
          {
            content: popoverContent,
            preferredPlacement: PopoverPlacement.TOP,
            children: /* @__PURE__ */ jsx(ChildrenWrapper, { children })
          }
        ),
        hintMessage && /* @__PURE__ */ jsx(InputHintMessage, { id: getChildElementId(inputId, "hint"), children: hintMessage }),
        errorMessage && /* @__PURE__ */ jsx(InputErrorMessage, { id: getChildElementId(inputId, "error"), children: errorMessage }),
        !errorMessage && warningMessage && /* @__PURE__ */ jsx(InputWarningMessage, { id: getChildElementId(inputId, "warning"), children: warningMessage })
      ]
    }
  )
);
var GroupFieldLayout = /* @__PURE__ */ ((GroupFieldLayout2) => {
  GroupFieldLayout2[GroupFieldLayout2["VERTICAL"] = 1] = "VERTICAL";
  GroupFieldLayout2[GroupFieldLayout2["HORIZONTAL"] = 2] = "HORIZONTAL";
  GroupFieldLayout2[GroupFieldLayout2["JUSTIFIED"] = 3] = "JUSTIFIED";
  return GroupFieldLayout2;
})(GroupFieldLayout || {});
const getGroupFieldLayout = (props) => {
  const groupFieldLayout = parseInt(`${props.$groupFieldLayout}`, 10);
  if (groupFieldLayout === GroupFieldLayout.HORIZONTAL) {
    return css`
      flex-direction: row;
      gap: ${s32};
    `;
  }
  if (groupFieldLayout === GroupFieldLayout.JUSTIFIED) {
    return css`
      flex-direction: row;
      justify-content: space-between;
    `;
  }
  return css`
    flex-direction: column;
    align-items: baseline;
    gap: ${s8};
  `;
};
const getIconColor = ({
  $hasError,
  $hasWarning,
  $isDisabled,
  $isActive
}) => {
  if ($isDisabled) return colors.iconActionDisabled;
  if ($hasError) return colors.iconError;
  if ($hasWarning) return colors.iconWarning;
  if ($isActive) return colors.iconActionActive;
  return colors.iconAction;
};
const GroupFieldItem = styled.div`
  ${text1};
  align-items: center;
  display: flex;
  margin: 0;
  cursor: pointer;
  color: ${colors.textDefault};

  /* used in order to place <CheckboxFocusBox /> or <RadioFocusBox /> */
  position: relative;

  & > ${ICON_SELECTOR} {
    margin-right: ${s8};
    color: ${getIconColor};
  }

  & .radio-icon-wrapper {
    /* this is just a quick fix for the radio icon that is wrapped with one more div
       we should not break encapsulation and rely on nesting in our styling */
    color: ${getIconColor};
  }

  &:focus-visible {
    outline: 0;
  }

  ${({ $isDisabled }) => $isDisabled && css`
      pointer-events: none;
      color: ${colors.textDisabled};
    `}
`;
const GroupFieldWrapper = styled.div`
  display: flex;
  ${getGroupFieldLayout};
`;
const GroupField = ({
  children,
  groupFieldLayout = GroupFieldLayout.VERTICAL,
  ...rest
}) => /* @__PURE__ */ jsx(Field, { ...rest, children: /* @__PURE__ */ jsx(GroupFieldWrapper, { $groupFieldLayout: groupFieldLayout, children }) });
const CheckboxFieldItemWrapper = styled(GroupFieldItem).attrs({
  as: "label"
})`
  position: relative;
`;
const HiddenCheckbox = styled.input.attrs({ type: "checkbox" })`
  border: 0px;
  clip: rect(0px, 0px, 0px, 0px);
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0px;
  position: absolute;
  width: 1px;
  white-space: nowrap;
`;
const CheckboxFieldItem = ({
  isDisabled,
  isLoading,
  isChecked,
  isMixed,
  hasError,
  hasWarning,
  children,
  onChange,
  preventDefault,
  stopPropagation,
  onShiftClick,
  ...rest
}) => {
  const { isFocusVisible, focusProps } = useFocusRing();
  return /* @__PURE__ */ jsxs(
    CheckboxFieldItemWrapper,
    {
      $isDisabled: isDisabled || isLoading,
      $isActive: !isLoading && (Boolean(isChecked) || isMixed),
      $hasError: !isLoading && hasError,
      $hasWarning: !isLoading && hasWarning,
      onClick: (event) => {
        if (event.nativeEvent.shiftKey && onShiftClick) {
          onShiftClick();
        }
      },
      ...rest,
      children: [
        isLoading ? /* @__PURE__ */ jsx(SpinnerIcon, {}) : /* @__PURE__ */ jsx(
          FocusableCheckboxIcon,
          {
            isChecked,
            isMixed,
            isFocused: isFocusVisible
          }
        ),
        /* @__PURE__ */ jsx(
          HiddenCheckbox,
          {
            ...focusProps,
            onClick: (event) => {
              if (preventDefault) event.preventDefault();
              if (stopPropagation) event.stopPropagation();
            },
            onChange: (event) => {
              if (preventDefault) event.preventDefault();
              if (stopPropagation) event.stopPropagation();
              onChange == null ? void 0 : onChange(!isChecked);
            },
            checked: isChecked === true
          }
        ),
        children
      ]
    }
  );
};
const Checkbox = ({
  isDisabled,
  isChecked,
  isMixed,
  isOptional,
  hasError,
  hasWarning,
  children,
  onChange,
  errorMessage,
  warningMessage,
  helperText,
  hintMessage,
  label,
  onCloseFieldClick,
  closeFieldIconTooltip,
  dataTestId,
  popoverHelpContent,
  popoverContent,
  onShiftClick,
  ...rest
}) => /* @__PURE__ */ jsx(
  Field,
  {
    isDisabled,
    isOptional,
    label,
    helperText,
    onCloseFieldClick,
    closeFieldIconTooltip,
    hintMessage,
    errorMessage,
    warningMessage,
    dataTestId,
    popoverHelpContent,
    popoverContent,
    children: /* @__PURE__ */ jsx(FlexBox, { children: /* @__PURE__ */ jsx(
      CheckboxFieldItem,
      {
        isDisabled,
        hasError: hasError || Boolean(errorMessage),
        hasWarning: hasWarning || Boolean(warningMessage),
        isMixed,
        isChecked,
        onChange,
        onShiftClick,
        ...rest,
        children
      }
    ) })
  }
);
const CheckboxGroupContext = createContext({});
const CheckboxGroup = (props) => {
  const {
    children,
    label,
    isDisabled,
    errorMessage,
    warningMessage,
    hintMessage,
    groupFieldLayout,
    popoverHelpContent,
    options = []
  } = props;
  const state = useGroupState(props);
  return /* @__PURE__ */ jsx(CheckboxGroupContext.Provider, { value: state, children: /* @__PURE__ */ jsxs(
    GroupField,
    {
      label,
      isDisabled,
      hintMessage,
      errorMessage,
      warningMessage,
      groupFieldLayout,
      formSize: props.formSize,
      popoverHelpContent,
      children: [
        options.map(({ label: label2, ...rest }) => /* @__PURE__ */ jsx(CheckboxGroupItem, { ...rest, children: label2 }, rest.value)),
        children
      ]
    }
  ) });
};
const CheckboxGroupItem = ({
  isDisabled: isDisabledOption,
  children,
  value,
  dataTestId
}) => {
  const {
    isDisabled: isDisabledField,
    setCurrentValue,
    currentValue,
    hasError,
    hasWarning
  } = useContext(CheckboxGroupContext);
  const { isFocusVisible, focusProps } = useFocusRing();
  const isDisabled = isDisabledField || isDisabledOption;
  const currentGroupValue = currentValue || [];
  const toggleCheckboxValue = () => {
    const isCurrentItemSelected = currentGroupValue.includes(value);
    setCurrentValue == null ? void 0 : setCurrentValue(
      isCurrentItemSelected ? without(currentGroupValue, value) : uniq([...currentGroupValue, value])
    );
  };
  const isChecked = currentGroupValue.includes(value);
  return /* @__PURE__ */ jsxs(
    GroupFieldItem,
    {
      onClick: () => toggleCheckboxValue(),
      $isDisabled: isDisabled,
      $isActive: isChecked,
      $hasError: hasError,
      $hasWarning: hasWarning,
      tabIndex: isDisabled ? -1 : 0,
      ...focusProps,
      onKeyDown: (e) => {
        if (e.key === " ") {
          e.preventDefault();
          toggleCheckboxValue();
        }
      },
      "data-test-id": dataTestId,
      children: [
        /* @__PURE__ */ jsx(FocusableCheckboxIcon, { isChecked, isFocused: isFocusVisible }),
        children
      ]
    }
  );
};
const Group = ({
  option: { name, isChecked, options, ...rest },
  onChange
}) => {
  return /* @__PURE__ */ jsxs(Box, { marginBottom: "small", children: [
    /* @__PURE__ */ jsx(
      Checkbox,
      {
        name,
        isChecked,
        onChange: () => onChange(name, !isChecked),
        ...rest
      },
      name
    ),
    options && /* @__PURE__ */ jsx(Box, { marginY: "small", marginLeft: "xlarge", children: options.map((option) => /* @__PURE__ */ jsx(Group, { option, onChange }, option.name)) })
  ] });
};
function updateDeep(name, isChecked, option, parents) {
  const inheritChecked = parents && parents.some((o) => o.name === name) || option.name === name ? isChecked : option.isChecked;
  if (!option.options) {
    return { ...option, isChecked: inheritChecked };
  }
  const updatedOptions = option.options.map(
    (o) => updateDeep(name, isChecked, o, parents ? [...parents, option] : [option])
  );
  const allChecked = updatedOptions.every((o) => o.isChecked);
  const someChecked = updatedOptions.some((o) => o.isChecked);
  return {
    ...option,
    isChecked: allChecked,
    isMixed: someChecked && !allChecked,
    options: updatedOptions
  };
}
const CheckboxTree = ({ options, onChange }) => {
  const handleChange = useCallback(
    (name, isChecked) => {
      const updatedOptions = options.map((o) => updateDeep(name, isChecked, o));
      onChange(name, isChecked, updatedOptions);
    },
    [options, onChange]
  );
  return /* @__PURE__ */ jsx("div", { children: options.map((option) => /* @__PURE__ */ jsx(Group, { option, onChange: handleChange }, option.name)) });
};
var RadioGroupItemType = /* @__PURE__ */ ((RadioGroupItemType2) => {
  RadioGroupItemType2["DEFAULT"] = "default";
  RadioGroupItemType2["CARD"] = "card";
  return RadioGroupItemType2;
})(RadioGroupItemType || {});
const RadioFocusBox = styled.div`
  position: absolute;
  top: -1px;
  left: -1px;
  width: 26px;
  height: 26px;
  border: 2px solid ${colors.borderActionActive};
  border-radius: ${s16};
  pointer-events: none;
`;
const FocusableRadioIcon = ({
  isChecked,
  isFocused
}) => {
  const IconComponent = isChecked ? RadioSelectedIcon : RadioUnselectedIcon;
  return /* @__PURE__ */ jsxs(Wrapper, { className: "radio-icon-wrapper", children: [
    /* @__PURE__ */ jsx(IconComponent, {}),
    isFocused && /* @__PURE__ */ jsx(RadioFocusBox, {})
  ] });
};
const Wrapper = styled.div`
  margin: 1px; /* space for the focus box */
  position: relative; /* focus box has absolute positioning */
  line-height: 0; /* removes space below the icon */
`;
const getBorderColor = (props) => {
  if (props.isDisabled) return "borderActionDisabled";
  if (props.isChecked) return "borderFocus";
  return "borderAction";
};
const RadioGroupItemTypeWrapper = ({
  children,
  isChecked = false,
  isDisabled = false,
  groupItemType = RadioGroupItemType.DEFAULT
}) => {
  switch (groupItemType) {
    case RadioGroupItemType.CARD:
      return /* @__PURE__ */ jsx(
        Box,
        {
          padding: "medium",
          borderColor: getBorderColor({ isChecked, isDisabled }),
          borderRadius: "r8",
          backgroundColor: "surfaceDefault",
          children
        }
      );
    default:
      return /* @__PURE__ */ jsx("div", { children });
  }
};
const validGroupFieldProps = [
  "groupFieldLayout",
  "errorMessage",
  "hintMessage",
  "label",
  "isDisabled",
  "warningMessage",
  "helperText",
  "hasError",
  "hasWarning",
  "popoverHelpContent"
];
const RadioGroupItemContainer = styled(FlexBox)`
  &[aria-disabled='true'] {
    pointer-events: none;
  }
`;
const RadioGroupItemHelperTextContainer = styled(Box).attrs({
  marginBottom: "small",
  marginLeft: "xlarge"
})`
  max-width: 255px;
`;
const RadioGroupContext = createContext({});
const RadioGroup = ({ children, ...props }) => {
  const { options = [], groupItemType } = props;
  const state = useGroupState(props);
  return /* @__PURE__ */ jsx(RadioGroupContext.Provider, { value: state, children: /* @__PURE__ */ jsxs(GroupField, { ...pick(props, validGroupFieldProps), children: [
    options.map(({ label, ...rest }) => /* @__PURE__ */ jsx(
      RadioGroupItem,
      {
        groupItemType,
        ...rest,
        children: label
      },
      rest.value
    )),
    children
  ] }) });
};
const RadioGroupItem = ({
  value,
  children,
  isDisabled: isDisabledOption,
  groupItemType = RadioGroupItemType.DEFAULT,
  helperText,
  ...rest
}) => {
  const {
    setCurrentValue,
    currentValue,
    hasError,
    hasWarning,
    isDisabled: isDisabledField
  } = useContext(RadioGroupContext);
  const { isFocusVisible, focusProps } = useFocusRing();
  const isChecked = currentValue === value;
  const isDisabled = isDisabledOption || isDisabledField;
  return /* @__PURE__ */ jsx(
    RadioGroupItemContainer,
    {
      "aria-disabled": isDisabled,
      style: { cursor: !isDisabled ? "pointer" : "auto" },
      DEPRECATED_onClick: () => !isDisabled && (setCurrentValue == null ? void 0 : setCurrentValue(value)),
      children: /* @__PURE__ */ jsxs(
        RadioGroupItemTypeWrapper,
        {
          isChecked,
          groupItemType,
          children: [
            /* @__PURE__ */ jsx(
              GroupFieldItem,
              {
                $hasError: hasError,
                $hasWarning: hasWarning,
                $isDisabled: isDisabled,
                $isActive: isChecked,
                tabIndex: isDisabled ? -1 : 0,
                ...focusProps,
                ...rest,
                onKeyDown: (e) => {
                  if (e.key === " ") {
                    e.preventDefault();
                    setCurrentValue == null ? void 0 : setCurrentValue(value);
                  }
                },
                children: /* @__PURE__ */ jsxs(FlexBox, { gap: "small", align: "center", children: [
                  /* @__PURE__ */ jsx(
                    FocusableRadioIcon,
                    {
                      isChecked,
                      isFocused: isFocusVisible
                    }
                  ),
                  children
                ] })
              }
            ),
            helperText && /* @__PURE__ */ jsx(RadioGroupItemHelperTextContainer, { children: /* @__PURE__ */ jsx(
              Text,
              {
                variant: "caption",
                color: isDisabled ? "textDisabled" : "textSubtle",
                children: helperText
              }
            ) })
          ]
        }
      )
    }
  );
};
const RadioInput = ({
  id,
  name,
  value,
  isChecked,
  onChange
}) => {
  const [isFocused, setIsFocused] = useState(false);
  return /* @__PURE__ */ jsxs(RelativeWrapper, { onClick: onChange, children: [
    /* @__PURE__ */ jsx(FocusableRadioIcon, { isChecked, isFocused }),
    /* @__PURE__ */ jsx(
      HiddenRadioInput,
      {
        id,
        type: "radio",
        name,
        value,
        onFocus: () => setIsFocused(true),
        onBlur: () => setIsFocused(false),
        checked: isChecked,
        onChange
      }
    )
  ] });
};
const HiddenRadioInput = styled.input`
  /* This is the input that is hidden to provide better styling, but provides all accessibility features of radio input */
  opacity: 0;
  position: absolute;
`;
const RelativeWrapper = styled.div`
  position: relative;
`;
const getInputPaddingRight = ({
  $hasError,
  $hasWarning,
  $hasRightIcon,
  $hideErrorIcon,
  $hideWarningIcon
}) => {
  const ICON_WIDTH = 24;
  const ICON_PADDING = 8;
  const ICON_WITH_PADDING = ICON_WIDTH + ICON_PADDING;
  const PADDING_FOR_ICON_AND_ERROR = $hasError && $hideErrorIcon || $hasWarning && $hideWarningIcon ? ICON_WITH_PADDING + ICON_PADDING : 2 * ICON_WITH_PADDING + ICON_PADDING;
  const PADDING_FOR_ICON = $hideErrorIcon ? 2 * ICON_PADDING : ICON_WITH_PADDING + ICON_PADDING;
  if (($hasError || $hasWarning) && $hasRightIcon)
    return `${PADDING_FOR_ICON_AND_ERROR}px`;
  if ($hasError || $hasWarning || $hasRightIcon) return `${PADDING_FOR_ICON}px`;
  return s16;
};
const getInputPaddingLeft = ({
  $hasLeftIcon
}) => {
  if ($hasLeftIcon) return "40px";
  return s16;
};
const commonInputStyle = css`
  border: none;
  border-radius: ${r4};
  box-shadow: 0px 0px 0px 1px ${colors.borderAction};
  ${normal16};
  width: 100%;

  &:hover {
    box-shadow: 0px 0px 0px 1px ${colors.borderActionHover};
  }

  &&:focus {
    box-shadow: 0px 0px 0px 2px ${colors.borderActionActive};
  }

  ${({ $isFocused }) => $isFocused && css`
      && {
        box-shadow: 0px 0px 0px 2px ${colors.borderActionActive};
      }
    `}

  ${({ $hasError, $hasWarning }) => !$hasError && $hasWarning && warningCSS}

  ${({ $hasError }) => $hasError && errorCSS}

  padding: 6px ${getInputPaddingRight} 6px ${getInputPaddingLeft};
`;
const PresetIcon = styled(Icon)`
  z-index: 4;
  pointer-events: none;
  top: 6px;
  color: ${({ $isDisabled }) => $isDisabled ? colors.iconActionDisabled : colors.iconAction};
  &:first-child {
    left: ${s8};
  }
  &:last-child {
    right: ${s8};
  }
`;
const FormErrorIcon = styled(PresetIcon).attrs({
  iconName: IconName.ERROR
})`
  &&& {
    color: ${colors.iconError};
  }
`;
const FormWarningIcon = styled(PresetIcon).attrs({
  iconName: IconName.WARNING
})`
  &&& {
    color: ${colors.iconWarning};
  }
`;
const InputWrapper = styled.div`
  display: flex;
  position: relative;

  && ${FormErrorIcon}, && ${FormWarningIcon} {
    right: ${({ $hasRightIcon, $hasPostfix }) => $hasRightIcon || $hasPostfix ? "40px" : s8};
  }

  ${PresetIcon} {
    position: absolute;
  }

  ${({ $isDisabled }) => $isDisabled && css`
      pointer-events: none;
    `}
`;
const ReadOnlyStyle = styled.div`
  ${text1};
  flex: 1;
  /* In theory s4 should work here just fine,
     but in practice to make editable and read mode
     pixel perfect I am making this 5px exception */
  padding: 5px ${s16};
  background: ${colors.surfaceStatusNeutral};
  border: 1px solid ${colors.borderAction};
  white-space: pre-line;
  border-radius: ${r4};
  color: ${colors.textDefault};
  line-height: 24px;
  min-height: 36px;
  display: flex;
  align-items: center;

  ${({ $hasLeftIcon }) => $hasLeftIcon && css`
      padding-left: 40px;
    `}
`;
const TextInputStyle = styled.input`
  background-color: ${({ $isDisabled }) => $isDisabled ? colors.surfaceDisabled : colors.surfaceDefault};
  color: ${({ $isDisabled }) => $isDisabled ? colors.textDisabled : colors.textDefault};
  ${({ $isDisabled }) => $isDisabled && css`
      &::placeholder {
        color: ${colors.textDisabled};
      }
    `}

  &:read-only {
    background-color: ${colors.surfaceStatusNeutral};
    color: ${colors.textDefault};
  }

  position: relative;
  ${commonInputStyle};
  text-align: ${({ $alignText }) => $alignText};
  border-radius: ${({ $hasPrefix, $hasPostfix }) => `${$hasPrefix ? 0 : r4} ${$hasPostfix ? 0 : r4} ${$hasPostfix ? 0 : r4} ${$hasPrefix ? 0 : r4}`};
`;
const ClickableIcon = styled(PresetIcon)`
  && {
    cursor: pointer;
    pointer-events: auto;
    &:hover {
      color: ${colors.iconActionHover};
    }
  }
`;
const Msg = styled.div`
  ${text2Bold};
  white-space: nowrap;
  background-color: ${({ $isDisabled }) => $isDisabled ? colors.surfaceDisabled : colors.surfaceDefaultSubtle};
  padding: ${s8};
  color: ${({ $isDisabled }) => $isDisabled ? colors.textDisabled : colors.textDefault};
  box-shadow: 0px 0px 0px 1px ${colors.borderAction};
`;
const PrefixMsg = styled(Msg)`
  border-radius: ${r4} 0 0 ${r4};
`;
const PostfixMsg = styled(Msg)`
  border-radius: 0 ${r4} ${r4} 0;
`;
const RightIcon = ({
  iconName,
  onClick,
  tooltip,
  isDisabled,
  showClearIcon,
  onClear
}) => {
  if (showClearIcon)
    return /* @__PURE__ */ jsx(
      ClickableIcon,
      {
        onClick: () => {
          onClear == null ? void 0 : onClear();
        },
        $isDisabled: isDisabled,
        iconName: IconName.CLOSE,
        "data-tooltip-text": "Clear"
      }
    );
  if (iconName && onClick)
    return /* @__PURE__ */ jsx(
      ClickableIcon,
      {
        $isDisabled: isDisabled,
        iconName,
        onClick,
        "data-tooltip-text": tooltip
      }
    );
  if (iconName)
    return /* @__PURE__ */ jsx(
      PresetIcon,
      {
        iconName,
        $isDisabled: isDisabled,
        "data-tooltip-text": tooltip
      }
    );
  return null;
};
const getAutoCompleteProps = (props) => {
  if (props.autoComplete === "off") {
    return {
      "data-1p-ignore": true,
      // 1Password autocomplete
      "data-lpignore": true,
      // LastPass autocomplete
      "data-form-type": "other"
      // Dashlane autocomplete
    };
  }
};
const TextInput = forwardRef(
  ({
    leftIconName,
    rightIconName,
    rightIconOnClick,
    rightIconTooltip,
    onValueChange,
    errorMessage,
    warningMessage,
    helperText,
    hintMessage,
    isDisabled,
    isReadOnly,
    label,
    value,
    hideErrorIcon,
    hideWarningIcon,
    alignInputText = "left",
    displayValue,
    isOptional,
    formSize,
    prefix,
    prefixNode,
    postfix,
    postfixNode,
    onCloseFieldClick,
    closeFieldIconTooltip,
    __DS_ONLY_fieldStyle,
    dataTestId,
    popoverContent,
    popoverHelpContent,
    hasError: hasErrorProp,
    hasWarning: hasWarningProp,
    className,
    onClear,
    onCommit,
    ...props
  }, ref) => {
    const [isDisplayMode, setDisplayMode] = useState(Boolean(displayValue));
    const isCommitedRef = useRef(false);
    const generatedTextInputId = useId();
    const id = props.id ?? generatedTextInputId;
    const hasError = hasErrorProp || Boolean(errorMessage);
    const hasWarning = !hasError && (hasWarningProp || Boolean(warningMessage));
    const hasPrefix = Boolean(prefix || prefixNode);
    const hasPostfix = Boolean(postfix || postfixNode);
    const showClearIcon = Boolean(value) && Boolean(onClear);
    const autoCompleteProps = getAutoCompleteProps(props);
    const styleProps = {
      $isDisabled: isDisabled || isReadOnly,
      $hasError: hasError,
      $hasWarning: hasWarning,
      $hasLeftIcon: Boolean(leftIconName),
      $hasRightIcon: Boolean(rightIconName || showClearIcon),
      $alignText: alignInputText,
      $hideErrorIcon: hideErrorIcon,
      $hideWarningIcon: hideWarningIcon,
      $hasPrefix: hasPrefix,
      $hasPostfix: hasPostfix
    };
    const handleChange = useCallback(
      (newValue) => {
        onValueChange == null ? void 0 : onValueChange(newValue);
        isCommitedRef.current = true;
      },
      [onValueChange]
    );
    const handleBlur = (event) => {
      var _a;
      (_a = props.onBlur) == null ? void 0 : _a.call(props, event);
      if (isCommitedRef.current && onCommit) {
        onCommit(event.target.value);
        isCommitedRef.current = false;
      }
      if (displayValue === void 0) {
        return;
      }
      setDisplayMode(true);
    };
    return /* @__PURE__ */ jsx(
      Field,
      {
        label,
        inputId: id,
        isOptional,
        helperText,
        isDisabled,
        formSize,
        onCloseFieldClick,
        closeFieldIconTooltip,
        hintMessage,
        errorMessage,
        warningMessage,
        __DS_ONLY_fieldStyle,
        dataTestId,
        popoverContent,
        popoverHelpContent,
        className,
        children: /* @__PURE__ */ jsxs(InputWrapper, { ...styleProps, children: [
          prefixNode,
          prefix && /* @__PURE__ */ jsx(PrefixMsg, { ...styleProps, children: prefix }),
          leftIconName && /* @__PURE__ */ jsx(PresetIcon, { ...styleProps, iconName: leftIconName }),
          isDisplayMode ? /* @__PURE__ */ jsx(
            TextInputStyle,
            {
              id,
              ref,
              ...props,
              ...styleProps,
              value: displayValue,
              readOnly: isReadOnly,
              onFocus: () => setDisplayMode(false)
            }
          ) : /* @__PURE__ */ jsx(
            TextInputStyle,
            {
              ...props,
              ...autoCompleteProps,
              ...styleProps,
              id,
              ref,
              value,
              readOnly: isReadOnly,
              "aria-describedby": helperText ? getChildElementId(id, "help-text") : void 0,
              "aria-errormessage": hasError ? getChildElementId(id, "error") : void 0,
              "aria-invalid": hasError ? "true" : void 0,
              onChange: (e) => {
                var _a;
                const newValue = e.currentTarget.value;
                handleChange(newValue);
                (_a = props.onChange) == null ? void 0 : _a.call(props, e);
              },
              onBlur: handleBlur
            }
          ),
          !showClearIcon && hasError && !hideErrorIcon && /* @__PURE__ */ jsx(FormErrorIcon, {}),
          !showClearIcon && !hasError && hasWarning && !hideWarningIcon && /* @__PURE__ */ jsx(FormWarningIcon, {}),
          /* @__PURE__ */ jsx(
            RightIcon,
            {
              iconName: rightIconName,
              onClick: rightIconOnClick,
              tooltip: rightIconTooltip,
              showClearIcon,
              isDisabled,
              onClear
            }
          ),
          postfix && /* @__PURE__ */ jsx(PostfixMsg, { ...styleProps, children: postfix }),
          postfixNode
        ] })
      }
    );
  }
);
const UpDownWrapper = styled.div`
  box-shadow: 0px 0px 0px 1px ${colors.borderAction};
  border-radius: 0 ${r4} ${r4} 0;
  width: 32px;
  min-width: 32px;
  height: 36px;
  background-color: ${colors.surfaceActionSecondary};
  user-select: none;
`;
const UpDownButton = styled.div`
  text-align: center;
  height: 50%;
  color: ${colors.iconAction};
  ${ICON_SELECTOR} {
    font-size: 18px !important;
  }

  ${({ disabled }) => disabled ? css`
          color: ${colors.iconActionDisabled};
        ` : css`
          &:hover {
            background-color: ${colors.surfaceActionSecondaryHover};
            cursor: pointer;
            ${ICON_SELECTOR} {
              color: ${colors.iconActionHover};
            }
          }
        `}
`;
const UpDown = ({
  onUpClick,
  onDownClick,
  upArrowDisabled,
  downArrowDisabled
}) => /* @__PURE__ */ jsxs(UpDownWrapper, { children: [
  /* @__PURE__ */ jsx(
    UpDownButton,
    {
      disabled: upArrowDisabled,
      onClick: upArrowDisabled ? void 0 : onUpClick,
      "data-testid": "up-arrow",
      children: /* @__PURE__ */ jsx(ChevronUpIcon, {})
    }
  ),
  /* @__PURE__ */ jsx(
    UpDownButton,
    {
      disabled: downArrowDisabled,
      onClick: downArrowDisabled ? void 0 : onDownClick,
      "data-testid": "down-arrow",
      children: /* @__PURE__ */ jsx(ChevronDownIcon, {})
    }
  )
] });
const NumberInput = forwardRef(
  ({
    hasError,
    defaultValue,
    value,
    onValueChange,
    allowDecimals,
    allowBlank = true,
    displayValue,
    arrowClickStepSize = 1,
    showUpDownArrows = true,
    upArrowDisabled = false,
    downArrowDisabled = false,
    min,
    max,
    ...props
  }, ref) => {
    const parseFn = useCallback(
      (input) => allowDecimals ? parseFloat(input) : parseInt(input, 10),
      [allowDecimals]
    );
    const [textInputValue, setTextInputValue] = useState(
      Number.isFinite(value) && `${value}` || Number.isFinite(defaultValue) && `${defaultValue}` || (allowBlank ? "" : "0")
    );
    useEffect(() => {
      if (parseFn(textInputValue) !== value) {
        const valueToSet = Number.isFinite(value) && `${value}` || (allowBlank ? "" : "0");
        setTextInputValue(valueToSet);
      }
    }, [value, allowBlank, parseFn]);
    const getArrowClickHandler = (step) => () => {
      const parsedValue = parseFn(textInputValue);
      const newValue = (parsedValue || 0) + step;
      if (min !== void 0 && newValue < min) return;
      if (max !== void 0 && newValue > max) return;
      setTextInputValue(`${newValue}`);
      onValueChange == null ? void 0 : onValueChange(parseFn(`${newValue}`));
    };
    const handleChange = (value2) => {
      const parsedValue = parseFn(value2);
      setTextInputValue(value2);
      const fallbackValue = allowBlank ? "" : "0";
      const newValue = `${parsedValue || parsedValue === 0 ? parsedValue : fallbackValue}`;
      onValueChange == null ? void 0 : onValueChange(parseFn(`${newValue}`));
    };
    return /* @__PURE__ */ jsx(
      TextInput,
      {
        ...props,
        ref,
        hasError,
        hideErrorIcon: true,
        value: textInputValue,
        onValueChange: (number) => {
          handleChange(number);
        },
        displayValue,
        postfixNode: showUpDownArrows && /* @__PURE__ */ jsx(
          UpDown,
          {
            onUpClick: getArrowClickHandler(arrowClickStepSize),
            onDownClick: getArrowClickHandler(-arrowClickStepSize),
            upArrowDisabled,
            downArrowDisabled
          }
        ),
        onBlur: () => {
          const parsedValue = parseFn(textInputValue);
          const fallbackValue = allowBlank ? "" : "0";
          const newValue = `${parsedValue || parsedValue === 0 ? parsedValue : fallbackValue}`;
          setTextInputValue(newValue);
          onValueChange == null ? void 0 : onValueChange(parseFn(newValue));
        }
      }
    );
  }
);
const getIsWhite = (color) => {
  if (!getIsCssColorValue(color)) return false;
  return color.toUpperCase().match(/^#(?:[F]{3,6})$/);
};
const FULL_TRANSPARENT = "#ffffff00";
const getInputIcon = (value, ignoreTransparencies) => {
  if (value === "") return FULL_TRANSPARENT;
  if (getIsWhite(value)) return IconName.CHECKBOX_UNCHECKED;
  if (getIsCssColorValue(value, { ignoreTransparencies })) {
    return value;
  }
  return IconName.KNOWLEDGE_BASE;
};
const HexInput = forwardRef(
  ({
    hasError,
    defaultValue,
    value,
    fallbackColor = "",
    onValueChange,
    skipIcon,
    ignoreTransparencies,
    isColorPickerOpen,
    onClear,
    ...props
  }, ref) => {
    const [lastValidValue, setlastValidValue] = useState(
      getIsCssColorValue(value || "", { ignoreTransparencies }) && value || getIsCssColorValue(defaultValue || "", { ignoreTransparencies }) && defaultValue || fallbackColor
    );
    const [textInputValue, setTextInputValue] = useState(lastValidValue);
    useEffect(() => {
      const valueToSet = value || fallbackColor;
      if (value && getIsCssColorValue(valueToSet, { ignoreTransparencies })) {
        setlastValidValue(valueToSet);
        setTextInputValue(valueToSet);
      } else {
        setlastValidValue(fallbackColor);
        setTextInputValue(fallbackColor);
      }
    }, [value, fallbackColor, ignoreTransparencies]);
    return /* @__PURE__ */ jsx(
      TextInput,
      {
        ...props,
        onClear,
        ref,
        __DS_ONLY_fieldStyle: isColorPickerOpen && {
          position: "relative",
          // When color picker dropdown is opened
          // we need to render input on the top of
          // backdrop so it stays interactive
          zIndex: zIndex.MODAL_BACKDROP + 1
        } || void 0,
        hasError,
        hideErrorIcon: true,
        leftIconName: !skipIcon ? getInputIcon(textInputValue, ignoreTransparencies) : void 0,
        value: textInputValue,
        onValueChange: (newValue) => {
          const withHash = `#${newValue}`.replace(/^#+/, "#");
          if (newValue === "") {
            setlastValidValue(fallbackColor);
            setTextInputValue("");
          } else if (getIsCssColorValue(withHash, { ignoreTransparencies })) {
            setlastValidValue(withHash.toUpperCase());
            setTextInputValue(withHash);
            onValueChange == null ? void 0 : onValueChange(withHash);
          } else {
            setTextInputValue(newValue);
          }
        },
        onBlur: () => {
          var _a;
          setTextInputValue(lastValidValue);
          onValueChange == null ? void 0 : onValueChange(lastValidValue);
          (_a = props.onBlur) == null ? void 0 : _a.call(props);
        }
      }
    );
  }
);
const SearchInput = forwardRef(
  ({
    value,
    debounceTime,
    onValueChange: onValueChangeProp,
    placeholder = "Search...",
    dataClickId,
    dataTestId,
    ...props
  }, ref) => {
    const [localValue, setLocalValue] = useState(value || "");
    const debouncedOnValueChange = useCallback(
      debounce(onValueChangeProp, debounceTime || 250),
      []
    );
    const onValueChange = (newValue) => {
      setLocalValue(newValue);
      debouncedOnValueChange(newValue);
    };
    return /* @__PURE__ */ jsx(
      TextInput,
      {
        __DS_ONLY_fieldStyle: { flexShrink: 0 },
        formSize: FormSize.MEDIUM,
        placeholder,
        "data-click-id": dataClickId ?? "type-to-filter",
        "data-test-id": dataTestId ?? "search-input",
        leftIconName: IconName.SEARCH,
        ref,
        value: localValue,
        onValueChange,
        onClear: () => onValueChange(""),
        ...props
      }
    );
  }
);
SearchInput.displayName = "SearchInput";
const PasswordInput = forwardRef(
  (props, ref) => {
    const [isVisible, setIsVisible] = useState(false);
    const toggleVisible = () => setIsVisible(!isVisible);
    return /* @__PURE__ */ jsx(
      TextInput,
      {
        ref,
        type: isVisible ? "text" : "password",
        rightIconName: isVisible ? IconName.VISIBILITY_OFF : IconName.VISIBILITY,
        rightIconOnClick: toggleVisible,
        rightIconTooltip: isVisible ? "Hide" : "Show",
        ...props
      }
    );
  }
);
const TextAreaStyle = styled.textarea`
  ${commonInputStyle};
  resize: none;
  ${({ $isDisabled }) => $isDisabled && css`
      background-color: ${colors.surfaceDisabled};
      color: ${colors.textDisabled};
    `}
  ${({ $height }) => $height ? `height: ${$height};` : ""}
`;
const TextArea = forwardRef(
  ({
    isDisabled,
    isReadOnly,
    isOptional,
    warningMessage,
    errorMessage,
    helperText,
    hintMessage,
    label,
    hasWarning: hasWarningProp,
    hideWarningIcon,
    value,
    fixedHeight,
    rows = 3,
    onChange,
    onCommit,
    onValueChange,
    popoverHelpContent,
    ...props
  }, forwardedRef) => {
    const { ref } = useTextAreaAutosize(value);
    useImperativeHandle(forwardedRef, () => ref.current);
    const isCommitedRef = useRef(false);
    const hasError = props.hasError || Boolean(errorMessage);
    const hasWarning = !hasError && (hasWarningProp || Boolean(warningMessage));
    const styleProps = {
      $isDisabled: isDisabled,
      $hasError: hasError,
      $hasWarning: hasWarning,
      $hideWarningIcon: hideWarningIcon
    };
    const handleChange = (event) => {
      const newValue = event.target.value;
      onChange == null ? void 0 : onChange(event);
      onValueChange == null ? void 0 : onValueChange(newValue);
      isCommitedRef.current = true;
    };
    const handleBlur = (event) => {
      if (isCommitedRef.current && onCommit) {
        onCommit(event.target.value);
        isCommitedRef.current = false;
      }
    };
    return /* @__PURE__ */ jsx(
      Field,
      {
        label,
        helperText,
        isDisabled,
        isOptional,
        hintMessage,
        errorMessage,
        warningMessage,
        popoverHelpContent,
        children: /* @__PURE__ */ jsxs(InputWrapper, { ...styleProps, children: [
          isReadOnly ? /* @__PURE__ */ jsx(ReadOnlyStyle, { children: value }) : /* @__PURE__ */ jsx(
            TextAreaStyle,
            {
              ...props,
              ...styleProps,
              onBlur: handleBlur,
              onChange: handleChange,
              value,
              ref: fixedHeight ? void 0 : ref,
              $height: fixedHeight ? "100%" : void 0,
              disabled: isDisabled,
              rows
            }
          ),
          hasError && /* @__PURE__ */ jsx(FormErrorIcon, {}),
          !hasError && hasWarning && !hideWarningIcon && /* @__PURE__ */ jsx(FormWarningIcon, {})
        ] })
      }
    );
  }
);
const FormFooter = styled.div``;
const FORM_FOOTER_SELECTOR = FormFooter.toString();
const FormWrapper = styled(Space).attrs({
  $isVertical: true
})`
  width: ${({ $formSize = FormSize.DEFAULT }) => $formSize};

  ${FORM_FOOTER_SELECTOR} {
    margin-top: ${s16};
  }

  & > ${BUTTON_SELECTOR} {
    align-self: start;
  }
`;
const FORM_WRAPPER_SELECTOR = FormWrapper.toString();
const THUMB_SIZE = 20;
const BAR_HEIGHT = 6;
const SLIDER_HEIGHT = 24;
const VALUE_WIDTH = 24;
const SliderWrapper = styled.div`
  display: flex;
`;
const SliderBarWrapper = styled.div`
  flex: 1;
  height: ${SLIDER_HEIGHT}px;
  position: relative;
  min-width: 180px;
`;
const SliderBar = styled.div`
  position: absolute;
  top: calc(50% - ${BAR_HEIGHT / 2}px);
  background-color: ${({ $barColor }) => $barColor ?? colors.borderAction};
  height: ${BAR_HEIGHT}px;
  width: 100%;
  border-radius: ${r4};
  cursor: pointer;
`;
const SliderValueBar = styled(SliderBar)`
  background-color: ${({ $hasError, $valueBarColor }) => $hasError ? colors.borderError : $valueBarColor ?? colors.borderInfo};
  pointer-events: none;
`;
const getThumbColor = ({
  $isDragging,
  $hasError,
  $thumbColor
}) => {
  if ($hasError && $isDragging) return colors.surfaceActionDeletePressed;
  if ($hasError) return colors.surfaceActionDelete;
  if ($thumbColor) return $thumbColor;
  if ($isDragging) return colors.surfaceActionPrimaryFocused;
  return colors.surfaceActionPrimary;
};
const SliderThumbCircle = styled.div`
  position: absolute;
  top: ${(SLIDER_HEIGHT - THUMB_SIZE) / 2}px;
  margin-left: -${THUMB_SIZE / 2}px;
  height: ${THUMB_SIZE}px;
  width: ${THUMB_SIZE}px;
  background-color: ${getThumbColor};
  border-radius: ${THUMB_SIZE / 2}px;
  cursor: pointer;

  transition: transform 0.1s ease-in-out;
  &:hover {
    transform: scale(1.1);
  }

  ${({ $isFocused }) => $isFocused && css`
      ${focusRingCSS};
      &::before {
        border-radius: ${THUMB_SIZE / 2}px;
      }
    `};
`;
const SliderValue = styled.div`
  ${normal16};
  color: ${({ $hasError }) => $hasError ? colors.textError : colors.textDefault};
  min-width: ${VALUE_WIDTH}px;
  margin-left: 12px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const InfinitySymbol = styled.div.attrs({ children: "∞" })`
  font-size: 22px;
`;
const SliderThumb = ({ percentValue, ...props }) => {
  const { state, trackRef, index, hasError, thumbColor } = props;
  const inputRef = useRef(null);
  const { thumbProps, inputProps } = useSliderThumb(
    { index, trackRef, inputRef },
    state
  );
  const { focusProps, isFocusVisible } = useFocusRing();
  return /* @__PURE__ */ jsx(
    SliderThumbCircle,
    {
      ...thumbProps,
      style: { left: `${percentValue}%` },
      $isDragging: state.isThumbDragging(index),
      $isFocused: isFocusVisible,
      $hasError: hasError,
      $thumbColor: thumbColor,
      children: /* @__PURE__ */ jsx(VisuallyHidden, { children: /* @__PURE__ */ jsx("input", { ref: inputRef, ...mergeProps(inputProps, focusProps) }) })
    }
  );
};
const AllocateWidth = styled.div`
  visibility: hidden;
  height: 0;
`;
const SliderInput = ({
  hasError,
  value,
  defaultValue,
  isValueLabelVisible = true,
  isUnlimited,
  thumbColor,
  barColor,
  valueBarColor,
  substituteMax,
  substituteMaxLabel,
  ...props
}) => {
  const trackRef = useRef(null);
  const numberFormatter = useNumberFormatter();
  const [sliderValue, setSliderValue] = useState(
    isNumber(value) ? substituteMax && value === substituteMax ? props.maxValue : value : void 0
  );
  const computedProps = useMemo(
    () => ({
      minValue: 0,
      step: 1,
      ...props,
      onChange: ([newValue]) => {
        var _a;
        setSliderValue(newValue);
        (_a = props.onChange) == null ? void 0 : _a.call(props, newValue);
      },
      onChangeEnd: ([newValue]) => {
        var _a;
        return (_a = props.onChangeEnd) == null ? void 0 : _a.call(props, newValue);
      },
      value: isNumber(sliderValue) ? [sliderValue] : void 0,
      defaultValue: isNumber(defaultValue) ? [defaultValue] : void 0,
      numberFormatter,
      // Added for aria-label related warnings
      label: props.label || "slider"
    }),
    [sliderValue, setSliderValue, defaultValue, numberFormatter, props]
  );
  const state = useSliderState(computedProps);
  const { groupProps, trackProps } = useSlider(computedProps, state, trackRef);
  const thumbValueLabel = state.getThumbValueLabel(0);
  const isMaxValueSelected = state.getThumbValue(0) === props.maxValue;
  const percentValue = Math.min(state.getThumbPercent(0) * 100, 100);
  return /* @__PURE__ */ jsxs(SliderWrapper, { ...groupProps, children: [
    /* @__PURE__ */ jsxs(SliderBarWrapper, { children: [
      /* @__PURE__ */ jsx(SliderBar, { ...trackProps, ref: trackRef, $barColor: barColor }),
      /* @__PURE__ */ jsx(
        SliderValueBar,
        {
          style: { width: `${percentValue}%` },
          $hasError: hasError,
          $valueBarColor: valueBarColor
        }
      ),
      /* @__PURE__ */ jsx(
        SliderThumb,
        {
          index: 0,
          state,
          percentValue,
          trackRef,
          hasError,
          thumbColor
        }
      )
    ] }),
    isValueLabelVisible && /* @__PURE__ */ jsxs(SliderValue, { children: [
      isMaxValueSelected ? substituteMaxLabel || (isUnlimited ? /* @__PURE__ */ jsx(InfinitySymbol, {}) : substituteMax ?? thumbValueLabel) : thumbValueLabel,
      /* allocate width for the substituteMaxLabel by always rendering it hidden */
      substituteMaxLabel && /* @__PURE__ */ jsx(AllocateWidth, { children: substituteMaxLabel })
    ] })
  ] });
};
const Slider = ({
  label,
  isDisabled,
  hintMessage,
  errorMessage,
  warningMessage,
  popoverHelpContent,
  ...sliderInputProps
}) => {
  const hasError = sliderInputProps.hasError || Boolean(errorMessage);
  return /* @__PURE__ */ jsx(
    Field,
    {
      label,
      isDisabled,
      hintMessage,
      errorMessage,
      warningMessage,
      popoverHelpContent,
      children: /* @__PURE__ */ jsx(
        SliderInput,
        {
          ...sliderInputProps,
          label,
          hasError,
          isDisabled
        }
      )
    }
  );
};
const SwitchLabel = styled.label`
  ${text1};
  line-height: 24px;
  display: inline;
  padding: 0 0 0 42px;
  vertical-align: middle;
  cursor: pointer;
  z-index: 0;

  ${({ $isChecked }) => $isChecked ? css`
          &:before {
            background-color: ${colors.surfaceSuccessStrong};
          }
          &:after {
            background-color: ${colors.surfaceDefault};
          }
        ` : css`
          &:before {
            background-color: ${colors.componentSwitchSurfaceDefault};
          }
          &:after {
            background-color: ${colors.surfaceDefault};
          }
        `}

  ${({ $isDisabled }) => $isDisabled && css`
      pointer-events: none;
      color: ${colors.textDisabled};
      &:before {
        background-color: ${colors.surfaceDisabled};
      }
      &:after {
        background-color: ${colors.surfaceDisabled};
      }
    `}

  &:before {
    content: '';
    position: absolute;
    top: 2px;
    left: 0;
    width: 34px;
    height: 20px;
    border-radius: 14px;
    z-index: 1;
    ${fade}
    transition-property: background-color;
  }

  &:after {
    content: '';
    position: absolute;
    top: 4px;
    left: 2px;
    width: ${space.medium};
    height: ${space.medium};
    box-shadow: ${shadowS};
    border-radius: 14px;
    z-index: 2;
    ${fade}
    transition-property: left, background-color, border-color;

    ${(props) => props.$isChecked && css`
        left: 16px;
        border-color: transparent;
      `}
  }
`;
const Container = styled.div`
  display: inline-flex;
  position: relative;
  line-height: 24px;
  height: 24px;

  &&:has(${HiddenCheckbox}:focus-visible) {
    ${SwitchLabel}:before {
      ${getFocusRingBorderCSS()}
    }
  }

  ${({ $isDisabled }) => $isDisabled && css`
      pointer-events: none;
    `}
`;
const Switch = (props) => {
  const {
    name,
    label,
    isChecked,
    onChange,
    isDisabled,
    helperText,
    dataTestId,
    formSize,
    alignItemsCenter,
    errorMessage,
    warningMessage
  } = props;
  const handleChange = useCallback(
    (event) => {
      onChange(event.target.checked);
    },
    [onChange]
  );
  return /* @__PURE__ */ jsxs(
    FieldWrapper,
    {
      "data-test-id": dataTestId,
      $formSize: formSize,
      $alignItemsCenter: alignItemsCenter,
      children: [
        helperText && /* @__PURE__ */ jsx(HelperTextWrapper, { children: helperText }),
        /* @__PURE__ */ jsxs(Container, { $isDisabled: isDisabled, children: [
          /* @__PURE__ */ jsx(
            SwitchLabel,
            {
              $isDisabled: isDisabled,
              htmlFor: name,
              $isChecked: isChecked,
              children: label
            }
          ),
          /* @__PURE__ */ jsx(
            HiddenCheckbox,
            {
              disabled: isDisabled,
              id: name,
              checked: isChecked,
              onChange: handleChange,
              "aria-checked": isChecked
            }
          )
        ] }),
        errorMessage && /* @__PURE__ */ jsx(InputErrorMessage, { children: errorMessage }),
        !errorMessage && warningMessage && /* @__PURE__ */ jsx(InputWarningMessage, { children: warningMessage })
      ]
    }
  );
};
const FieldsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: ${s16};
  width: ${({ $formSize = FormSize.AUTO }) => $formSize};

  ${FIELD_SELECTOR} {
    width: 100%;
  }
`;
const SIDE_NOTE_WIDTH = "295px";
const FIELD_WIDTH = "520px";
const SideNote = styled(Space).attrs({
  $isVertical: true
})`
  ${text2};
  background-color: ${colors.surfaceStatusNeutral};
  border-radius: ${r8};
  padding: ${s16} ${s24};
  color: ${colors.textSubtle};
  width: ${SIDE_NOTE_WIDTH};
  min-width: ${SIDE_NOTE_WIDTH};
  flex-shrink: 0;
  align-self: ${({ $alignSelf = "flex-start" }) => $alignSelf};
`;
const FieldsWrapperBody = styled(Space).attrs({ $isVertical: true })`
  flex: 1;
  & > ${FIELD_SELECTOR} {
    max-width: ${FIELD_WIDTH};
    min-width: 320px;
  }
`;
const FieldsWrapper = ({
  children,
  sideNote,
  alignSideNote
}) => /* @__PURE__ */ jsxs(FlexBox, { gap: "medium", children: [
  /* @__PURE__ */ jsx(FieldsWrapperBody, { children }),
  sideNote && /* @__PURE__ */ jsx(SideNote, { $alignSelf: alignSideNote, children: sideNote })
] });
const useOptimistic = (value, onValueChange) => {
  const [internalValue, setInternalValue] = useState(
    value
  );
  useEffect(() => {
    setInternalValue(value);
  }, [value]);
  const updateValue = useCallback(
    (value2) => {
      setInternalValue(value2);
      onValueChange(value2);
    },
    [setInternalValue, onValueChange]
  );
  return [internalValue, updateValue];
};
const useTextAreaSelection = (textAreaRef) => {
  const [selection, setSelection] = useState();
  useEffect(() => {
    const listener = (e) => {
      const target = e.currentTarget;
      setSelection({
        start: target.selectionStart,
        end: target.selectionEnd,
        dir: target.selectionDirection
      });
    };
    const el = textAreaRef.current;
    el == null ? void 0 : el.addEventListener("select", listener);
    return () => el == null ? void 0 : el.removeEventListener("select", listener);
  }, [textAreaRef]);
  useEffect(() => {
    var _a;
    (_a = textAreaRef.current) == null ? void 0 : _a.setSelectionRange(
      (selection == null ? void 0 : selection.start) ?? -1,
      (selection == null ? void 0 : selection.end) ?? -1,
      (selection == null ? void 0 : selection.dir) ?? "none"
    );
  }, [selection, textAreaRef]);
  const setCaretPosition = (pos) => setSelection({
    start: pos,
    end: pos,
    dir: "none"
  });
  return setCaretPosition;
};
export {
  Checkbox,
  CheckboxGroup,
  CheckboxGroupItem,
  CheckboxTree,
  FIELD_SELECTOR,
  FORM_FOOTER_SELECTOR,
  FORM_WRAPPER_SELECTOR,
  FULL_TRANSPARENT,
  Field,
  FieldWrapper,
  FieldsRow,
  FieldsWrapper,
  FieldsWrapperBody,
  FocusableCheckboxIcon,
  FormErrorIcon,
  FormFooter,
  FormSize,
  FormWarningIcon,
  FormWrapper,
  GroupFieldLayout,
  HelperTextWrapper,
  HexInput,
  InputErrorMessage,
  InputHintMessage,
  InputWarningMessage,
  InputWrapper,
  LABEL_ICON_SELECTOR,
  LABEL_SELECTOR,
  Label,
  LabelHelpIconWithPopover,
  NumberInput,
  PasswordInput,
  PresetIcon,
  RadioGroup,
  RadioGroupItem,
  RadioGroupItemType,
  RadioInput,
  ReadOnlyStyle,
  SearchInput,
  SideNote,
  Slider,
  Switch,
  TextArea,
  TextInput,
  commonInputStyle,
  getChildElementId,
  useGroupState,
  useOptimistic,
  useTextAreaAutosize,
  useTextAreaSelection
};
//# sourceMappingURL=index.js.map
